import React, { useContext, useState, useEffect } from 'react';
import { LocationContext } from '../Components/locationContext';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, Timestamp, getDoc } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { db } from '../Firebase/Firebase';
import Navbar from '../Components/NavBarV2';
import Footer from '../Components/footer';
import MapShow from '../Components/mapShow';
import ReservationForm from '../Components/ReservationForm';
import ReservationList from '../Components/ReservationList';
import Modal from 'react-modal';
import Slider from 'rc-slider';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import 'rc-slider/assets/index.css';
import './Reservation.css';
import { QRCodeCanvas } from 'qrcode.react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

Modal.setAppElement('#root');

const Reservation = () => {
    const { selectedLocation, setSelectedLocation } = useContext(LocationContext);
    const [selectedDate, setSelectedDate] = useState(null);
    const [selectedStartTime, setSelectedStartTime] = useState(null);
    const [selectedEndTime, setSelectedEndTime] = useState(null);
    const [reservations, setReservations] = useState([]);
    const [modalIsOpen, setModalIsOpen] = useState(false);
    const [qrModalIsOpen, setQrModalIsOpen] = useState(false);
    const [currentEditReservation, setCurrentEditReservation] = useState(null);
    const [currentQrReservation, setCurrentQrReservation] = useState(null);
    const [locations, setLocations] = useState([]);
    const [spaces, setSpaces] = useState([]);
    const [selectedPlace, setSelectedPlace] = useState(null);
    const [selectedSpace, setSelectedSpace] = useState(null);
    const [isSpacePopupOpen, setIsSpacePopupOpen] = useState(false);
    const auth = getAuth();
    const user = auth.currentUser;

    useEffect(() => {
        const fetchReservations = async () => {
            try {
                const querySnapshot = await getDocs(collection(db, 'reservas'));
                const reservationsData = querySnapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                }));

                const placesSnapshot = await getDocs(collection(db, 'lugar'));
                const placesData = placesSnapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                }));

                const reservationsWithPlaceNames = reservationsData.map(reservation => {
                    const place = placesData.find(place => place.id === reservation.placeId);
                    return {
                        ...reservation,
                        placeName: place ? place.nombreL : 'Lugar desconocido'
                    };
                });

                setReservations(reservationsWithPlaceNames);
            } catch (error) {
                console.error("Error fetching reservations: ", error);
            }
        };

        fetchReservations();
    }, []);

    const fetchSpaces = async (placeId) => {
        try {
            const querySnapshot = await getDocs(collection(db, 'lugar', placeId, 'espacios'));
            const spacesData = querySnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
            return spacesData;
        } catch (error) {
            console.error("Error fetching spaces: ", error);
            return [];
        }
    };

    const handleSliderChange = ([start, end]) => {
        const newStartTime = new Date(selectedDate);
        newStartTime.setHours(Math.floor(start / 2), (start % 2) * 30, 0, 0);
        const newEndTime = new Date(selectedDate);
        newEndTime.setHours(Math.floor(end / 2), (end % 2) * 30, 0, 0);
        setSelectedStartTime(newStartTime);
        setSelectedEndTime(newEndTime);
    };

    const handleReserve = async () => {
        if (selectedDate && selectedStartTime && selectedEndTime && selectedSpace && selectedPlace) {
            const reservationStart = new Date(selectedStartTime);
            const reservationEnd = new Date(selectedEndTime);

            const now = new Date();
            if (reservationStart < now || reservationEnd < now) {
                toast.error("No puedes reservar en un intervalo pasado.");
                return;
            }

            const newReservation = {
                userId: user.uid,
                location: selectedSpace.nombreEspacio,
                start: Timestamp.fromDate(reservationStart),
                end: Timestamp.fromDate(reservationEnd),
                placeId: selectedPlace.id,
                spaceId: selectedSpace.id
            };

            if (isTimeRangeAvailable(newReservation)) {
                try {
                    await addDoc(collection(db, 'reservas'), newReservation);
                    setReservations([...reservations, newReservation]);
                    toast.success("Reserva confirmada!");
                } catch (error) {
                    console.error("Error adding reservation: ", error);
                }
            } else {
                toast.error("El intervalo seleccionado está ocupado.");
            }
        } else {
            toast.error("Por favor, selecciona una fecha, un intervalo de horas y un espacio.");
        }
    };

    const openEditModal = async (reservation) => {
        const placeSpaces = await fetchSpaces(reservation.placeId);
        setSpaces(placeSpaces);

        setCurrentEditReservation(reservation);
        setSelectedDate(reservation.start.toDate());
        setSelectedStartTime(reservation.start.toDate());
        setSelectedEndTime(reservation.end.toDate());

        const location = locations.find(loc => loc.id === reservation.placeId);
        setSelectedLocation(location);

        const space = placeSpaces.find(sp => sp.id === reservation.spaceId);
        setSelectedSpace(space);

        setModalIsOpen(true);
    };

    const openQrModal = (reservation) => {
        setCurrentQrReservation(reservation);
        setQrModalIsOpen(true);
    };

    const closeQrModal = () => {
        setQrModalIsOpen(false);
        setCurrentQrReservation(null);
    };

    const handleEditReservation = async () => {
        if (!selectedSpace) {
            toast.error("Por favor, selecciona un espacio válido.");
            return;
        }

        const updatedReservation = {
            userId: user.uid,
            location: selectedSpace.nombreEspacio,
            start: Timestamp.fromDate(selectedStartTime),
            end: Timestamp.fromDate(selectedEndTime),
            placeId: selectedPlace.id,
            spaceId: selectedSpace.id
        };

        if (currentEditReservation.userId !== user.uid) {
            toast.error("No tienes permiso para editar esta reserva.");
            return;
        }

        try {
            const reservationRef = doc(db, 'reservas', currentEditReservation.id);
            await updateDoc(reservationRef, updatedReservation);
            setReservations(reservations.map(res => (res.id === currentEditReservation.id ? { ...res, ...updatedReservation } : res)));
            toast.success("Reserva actualizada!");
            closeEditModal();
        } catch (error) {
            console.error("Error updating reservation: ", error);
        }
    };
    const handleDeleteReservation = async (id) => {
        const reservationToDelete = reservations.find(res => res.id === id);
        
        if (!reservationToDelete || reservationToDelete.userId !== user.uid) {
            toast.error("No tienes permiso para eliminar esta reserva o la reserva no existe.");
            return;
        }
    
        const now = new Date();
        const reservationStart = reservationToDelete.start.toDate();
        const hoursUntilStart = Math.max((reservationStart - now) / (1000 * 60 * 60), 0);
    
        let penalty = 0;
        let confirmMessage = '';
        
        // Verifica si la cancelación está dentro del rango de 12 horas
        if (hoursUntilStart < 12) {
            const durationHours = Math.ceil((reservationToDelete.end.toDate() - reservationToDelete.start.toDate()) / (1000 * 60 * 60));
            penalty = ((durationHours * 2) * 5) * 0.1; // Penalización del 10% de la duración de la reserva
            confirmMessage = `Vas a cancelar la reserva. Se te cobrará una penalización de ${penalty} coins. ¿Deseas continuar?`;
        } else {
            confirmMessage = '¿Estás seguro de que quieres cancelar esta reserva? Se te reembolsará el total de coins.';
        }
    
        if (!window.confirm(confirmMessage)) return;
    
        try {
            const reservationRef = doc(db, 'reservas', id);
            await deleteDoc(reservationRef);
    
            // Refund logic
            const durationHours = Math.ceil((reservationToDelete.end.toDate() - reservationToDelete.start.toDate()) / (1000 * 60 * 60));
            let coinsToRefund = 0;
    
            if (hoursUntilStart < 12) {
                // Penalización aplicada: reembolsar solo el 90% de las coins
                coinsToRefund = (durationHours * 2) * 5 - penalty;
            } else {
                // Reembolso total
                coinsToRefund = (durationHours * 2) * 5;
            }
    
            // Update user's coins
            const userDocRef = doc(db, 'usuarios', user.uid);
            const userDoc = await getDoc(userDocRef);
            
            if (userDoc.exists()) {
                const userData = userDoc.data();
                const currentCoins = userData.coins;
                await updateDoc(userDocRef, { coins: currentCoins + coinsToRefund });
            }
    
            setReservations(reservations.filter(res => res.id !== id));
            toast.error("Reserva eliminada y coins reembolsadas!");
         } catch (error) {
            console.error("Error deleting reservation: ", error);
            toast.error("Ocurrió un error al cancelar la reserva.");
        }
    };
    
    
    

    const isTimeRangeAvailable = ({ start, end }) => {
        return !reservations.some(reservation =>
            reservation.spaceId === selectedSpace.id &&
            ((start.toDate() >= reservation.start.toDate() && start.toDate() < reservation.end.toDate()) ||
                (end.toDate() > reservation.start.toDate() && end.toDate() <= reservation.end.toDate()) ||
                (start.toDate() <= reservation.start.toDate() && end.toDate() >= reservation.end.toDate()))
        );
    };

    const closeEditModal = () => {
        setModalIsOpen(false);
        setCurrentEditReservation(null);
    };

    const closeSpacePopup = () => {
        setIsSpacePopupOpen(false);
    };

    return (
        <div>
            <Navbar />
            <ToastContainer />

            <div className='reservation-container'>
                <div className="reservation-main-content">
                    <MapShow
                        onSelectSpace={setSelectedSpace}
                        onSelectPlace={setSelectedPlace}
                        onClosePopup={closeSpacePopup}
                    />
                </div>
                <ReservationForm
                    selectedSpace={selectedSpace}
                    selectedDate={selectedDate}
                    setSelectedDate={setSelectedDate}
                    selectedStartTime={selectedStartTime}
                    selectedEndTime={selectedEndTime}
                    handleSliderChange={handleSliderChange}
                    handleReserve={handleReserve}
                    reservations={reservations}
                    userId={user.uid}
                />
                <h2>Mis Reservas</h2>
                <ReservationList
                    reservations={reservations}
                    user={user}
                    openEditModal={openEditModal}
                    handleDeleteReservation={handleDeleteReservation}
                    openQrModal={openQrModal}
                />

                <Modal
                    isOpen={modalIsOpen}
                    onRequestClose={closeEditModal}
                    contentLabel="Editar Reserva"
                    className="reservation-modal"
                    overlayClassName="reservation-overlay"
                >
                    <h2>Editar Reserva</h2>
                    {currentEditReservation && (
                        <div>
                            <h3>Espacio:</h3>
                            <select className='reservation-select-location'
                                value={selectedSpace?.nombreEspacio || ''}
                                onChange={(e) => {
                                    const space = spaces.find(sp => sp.nombreEspacio === e.target.value);
                                    setSelectedSpace(space);
                                }}
                            >
                                {spaces.map(space => (
                                    <option key={space.id} value={space.nombreEspacio}>
                                        {space.nombreEspacio}
                                    </option>
                                ))}
                            </select>
                            <h3>Fecha:</h3>
                            <DatePicker
                                selected={selectedDate}
                                onChange={date => setSelectedDate(date)}
                                dateFormat="yyyy/MM/dd"
                            />
                            <h3>Intervalo de tiempo:</h3>
                            <div className='reservation-slider-container'>
                                <div className='reservation-slider'>
                                    <Slider
                                        range
                                        min={0}
                                        max={48}
                                        step={1}
                                        marks={{
                                            0: '00:00',
                                            12: '06:00',
                                            24: '12:00',
                                            36: '18:00',
                                            48: '24:00'
                                        }}
                                        onChange={handleSliderChange}
                                        value={[
                                            (selectedStartTime.getHours() * 2) + (selectedStartTime.getMinutes() / 30),
                                            (selectedEndTime.getHours() * 2) + (selectedEndTime.getMinutes() / 30)
                                        ]}
                                    />
                                </div>
                                <div>
                                    {selectedStartTime && selectedEndTime && (
                                        <p>
                                            Hora de inicio: {selectedStartTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}<br />
                                            Hora de fin: {selectedEndTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                        </p>
                                    )}
                                </div>
                            </div>
                            <button className="reservation-btn-edit" onClick={handleEditReservation}>Guardar Cambios</button>
                            <button className="reservation-btn-delete" onClick={() => handleDeleteReservation(currentEditReservation.id)}>Eliminar Reserva</button>
                            <button className="reservation-btn-edit" onClick={closeEditModal}>Cancelar</button>
                        </div>
                    )}
                </Modal>

                <Modal
                    isOpen={qrModalIsOpen}
                    onRequestClose={closeQrModal}
                    contentLabel="Código QR de Reserva"
                    className="reservation-modal"
                    overlayClassName="reservation-overlay"
                >
                    <div className="qr-code-container">
                        <h2>Código QR de Reserva</h2>
                        {currentQrReservation && (
                            <>
                                <QRCodeCanvas
                                    value={`Detalles de la Reserva:
                                        \nUsuario: ${user.uid}
                                        \nCorreo: ${user.email}
                                        \nUbicación: ${currentQrReservation.location}
                                        \nLugar: ${currentQrReservation.placeName}
                                        \nDesde: ${currentQrReservation.start.toDate().toLocaleString()}
                                        \nHasta: ${currentQrReservation.end.toDate().toLocaleString()}`}
                                    size={256}
                                    bgColor={"#ffffff"}
                                    fgColor={"#000000"}
                                    level={"H"}
                                    includeMargin={true}
                                />
                                <button className="reservation-btn-edit" onClick={closeQrModal}>Cerrar</button>
                            </>
                        )}
                    </div>
                </Modal>
            </div>
        </div>
    );
};

export default Reservation;
